/** @typedef {{ type: string, username: string, profileUrl?: string | null, analyticsEnabled?: boolean, linkedAt?: string | null }} CodingProfileEntry */

/** Platforms with live StackTrack analytics on the student coding dashboard. */
export const ANALYTICS_ENABLED_PLATFORMS = new Set(['leetcode', 'codeforces', 'hackerrank', 'codechef', 'geeksforgeeks']);

export const CODING_PROFILE_TYPE_OPTIONS = [
  { value: 'leetcode', label: 'LeetCode' },
  { value: 'github', label: 'GitHub' },
  { value: 'codeforces', label: 'Codeforces' },
  { value: 'codechef', label: 'CodeChef' },
  { value: 'hackerrank', label: 'HackerRank' },
  { value: 'geeksforgeeks', label: 'GeeksForGeeks' },
  { value: 'codestudio', label: 'CodeStudio' },
  { value: 'atcoder', label: 'AtCoder' },
];

export const CODING_PROFILE_TYPE_TO_FIELD = {
  leetcode: 'leetcodeUsername',
  github: 'githubUsername',
  codeforces: 'codeforcesUsername',
  codechef: 'codechefUsername',
  hackerrank: 'hackerrankUsername',
  geeksforgeeks: 'geeksforgeeksUsername',
  codestudio: 'codestudioUsername',
  atcoder: 'atcoderUsername',
};

/**
 * Read-only URL prefix shown before the username in forms (does not include the handle).
 * Full URLs are built at render time via `buildPlatformProfileUrl`.
 * @type {Record<string, string>}
 */
export const PLATFORM_PROFILE_URL_PREFIXES = {
  leetcode: 'https://leetcode.com/u/',
  github: 'https://github.com/',
  codeforces: 'https://codeforces.com/profile/',
  codechef: 'https://www.codechef.com/users/',
  hackerrank: 'https://www.hackerrank.com/profile/',
  geeksforgeeks: 'https://www.geeksforgeeks.org/user/',
  codestudio: 'https://www.naukri.com/code360/profile/',
  atcoder: 'https://atcoder.jp/users/',
};

/** Optional trailing segment after the encoded username. */
const PLATFORM_PROFILE_URL_SUFFIXES = {
  leetcode: '/',
  geeksforgeeks: '/',
};

/**
 * Per-platform handle format rules (client-side pre-validation before save).
 * Each entry: { pattern, minLen, maxLen, hint }
 */
export const PLATFORM_USERNAME_RULES = {
  leetcode:       { pattern: /^[a-zA-Z0-9_-]{2,30}$/,       hint: '2–30 chars · letters, digits, _ or -' },
  codeforces:     { pattern: /^[a-zA-Z0-9_-]{1,24}$/,        hint: '1–24 chars · letters, digits, _ or -' },
  codechef:       { pattern: /^[a-zA-Z0-9_./-]{3,30}$/,      hint: '3–30 chars · letters, digits, _, . or -' },
  hackerrank:     { pattern: /^[a-zA-Z0-9_-]{1,30}$/,        hint: 'Up to 30 chars · letters, digits, _ or -' },
  github:         { pattern: /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,37}[a-zA-Z0-9])?$/, hint: 'Up to 39 chars · letters, digits or -' },
  geeksforgeeks:  { pattern: /^[a-zA-Z0-9_./-]{3,50}$/,      hint: '3–50 chars · letters, digits, _, . or -' },
  atcoder:        { pattern: /^[a-zA-Z0-9_]{3,16}$/,         hint: '3–16 chars · letters, digits or _' },
  codestudio:     { pattern: /^[a-zA-Z0-9_./-]{3,50}$/,      hint: '3–50 chars · letters, digits, _, . or -' },
};

const EXTENSION_PROFILE_TYPES = new Set(CODING_PROFILE_TYPE_OPTIONS.map((o) => o.value));

/**
 * Sanitize codingProfiles for extension bootstrap (HS-8.4.1).
 * Allowlists type + username (+ optional profileUrl). Drops invalid rows.
 *
 * @param {unknown} input
 * @returns {{ type: string, username: string, profileUrl?: string }[]}
 */
export function sanitizeExtensionCodingProfiles(input) {
  if (!Array.isArray(input)) return [];
  const map = new Map();
  for (const item of input) {
    const type = String(item?.type ?? '').trim().toLowerCase();
    const username = String(item?.username ?? '').trim();
    if (!type || !username || !EXTENSION_PROFILE_TYPES.has(type)) continue;
    const row = { type, username };
    const profileUrl = item?.profileUrl != null ? String(item.profileUrl).trim() : '';
    if (profileUrl) row.profileUrl = profileUrl;
    map.set(type, row);
  }
  return [...map.values()];
}

/**
 * Validate a platform username against its known format rules.
 * Returns `null` when valid (or when no rule exists for the platform).
 * Returns a human-readable error string when invalid.
 *
 * @param {string} type  Platform key, e.g. 'codechef'
 * @param {string} username  Raw input (not yet trimmed)
 * @returns {string | null}
 */
export function validatePlatformUsername(type, username) {
  const platform = String(type ?? '').trim().toLowerCase();
  const rule = PLATFORM_USERNAME_RULES[platform];
  if (!rule) return null;
  const handle = String(username ?? '').trim();
  if (!handle) return 'Username is required';
  if (!rule.pattern.test(handle)) return `Invalid format — ${rule.hint}`;
  return null;
}

const ORDER = CODING_PROFILE_TYPE_OPTIONS.map((o) => o.value);

/**
 * @param {string} type
 * @returns {string | null}
 */
export function getPlatformProfileUrlPrefix(type) {
  const platform = String(type ?? '').trim().toLowerCase();
  return PLATFORM_PROFILE_URL_PREFIXES[platform] ?? null;
}

/**
 * Build a public profile URL for a supported platform (username only stored in DB).
 * @param {string} type
 * @param {string} username
 * @returns {string | null}
 */
export function buildPlatformProfileUrl(type, username) {
  const platform = String(type ?? '').trim().toLowerCase();
  const prefix = PLATFORM_PROFILE_URL_PREFIXES[platform];
  const handle = String(username ?? '').trim();
  if (!prefix || !handle) return null;

  const suffix = PLATFORM_PROFILE_URL_SUFFIXES[platform] ?? '';
  return `${prefix}${encodeURIComponent(handle)}${suffix}`;
}

/** @param {string} type */
export function isAnalyticsEnabledPlatform(type) {
  return ANALYTICS_ENABLED_PLATFORMS.has(String(type ?? '').trim());
}

/**
 * Build ordered `codingProfiles` from flat API user fields (GET /users/me).
 * @returns {CodingProfileEntry[]}
 */
export function userApiToCodingProfiles(userData) {
  const out = [];
  for (const type of ORDER) {
    const field = CODING_PROFILE_TYPE_TO_FIELD[type];
    const username = (userData[field] ?? '').toString().trim();
    if (username) {
      out.push({
        type,
        username,
        profileUrl: buildPlatformProfileUrl(type, username),
        analyticsEnabled: isAnalyticsEnabledPlatform(type),
      });
    }
  }
  return out;
}

/**
 * Flatten `codingProfiles` for legacy flat-field consumers.
 * @param {CodingProfileEntry[] | undefined} codingProfiles
 */
export function codingProfilesToApiFields(codingProfiles) {
  const flat = Object.fromEntries(
    Object.entries(CODING_PROFILE_TYPE_TO_FIELD).map(([, field]) => [field, ''])
  );
  for (const p of codingProfiles || []) {
    const field = CODING_PROFILE_TYPE_TO_FIELD[p.type];
    if (field) flat[field] = (p.username ?? '').toString().trim();
  }
  return flat;
}

export function labelForCodingProfileType(type) {
  return CODING_PROFILE_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type;
}

/**
 * Normalize API codingProfiles for local state.
 * Preserves `linkedAt` from either the top-level field or `validationMetadata.validatedAt`.
 * @param {unknown} list
 * @returns {CodingProfileEntry[]}
 */
export function normalizeCodingProfilesFromApi(list) {
  if (!Array.isArray(list)) return [];
  return list
    .map((p) => {
      const type = String(p?.type ?? '').trim();
      const username = String(p?.username ?? '').trim();
      if (!type || !username) return null;
      const linkedAt =
        p?.linkedAt ?? p?.validationMetadata?.validatedAt ?? null;
      return {
        type,
        username,
        profileUrl: buildPlatformProfileUrl(type, username),
        analyticsEnabled: isAnalyticsEnabledPlatform(type),
        ...(linkedAt ? { linkedAt } : {}),
      };
    })
    .filter(Boolean);
}
